import * as React from "react";
import { useAuth } from "@clerk/clerk-react";

import { posthog } from "@/instrument";
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider";

/**
 * Renderless companion to `TenantAuthFlowProvider`. Once the IbadaCloud
 * user + active workspace + active branch resolve, calls:
 *
 *   posthog.identify(localUserId)
 *   posthog.group("workspace", workspaceId, { name, structureType })
 *   posthog.group("branch", branchId, { name, branchType })
 *
 * Re-fires when any of the IDs change (workspace / branch switching),
 * and calls `posthog.reset()` once on Clerk sign-out so the anonymous
 * session that follows is not linked back to the prior identified
 * user.
 *
 * Identify is intentionally trait-less — Clerk already holds the user
 * profile and we do NOT want to ship names / emails to PostHog from
 * here. The strategy doc enforces this rule:
 * `docs/architecture/posthog-tracking-strategy.md`.
 */
export function PostHogIdentitySync(): null {
  const { isSignedIn } = useAuth();
  const { authMe, tenantAccess, authMeResolved, tenantAccessResolved } = useTenantAuthFlow();

  const localUserId = authMe?.auth.localUserId ?? null;
  const workspace = tenantAccess?.resolved.workspace ?? null;
  const branch = tenantAccess?.resolved.branch ?? null;
  const accessibleBranches = tenantAccess?.branchContext.accessibleBranches ?? [];
  // Only attribute the user to a workspace/branch group when access is actually GRANTED. On a denial
  // (/no-access?reason=tenant-access) the resolve still echoes a *suggested* workspace/branch, and grouping on
  // it spammed $groupidentify in a tight loop during the prod incident. Identify stays (the user IS signed in).
  const accessAllowed = tenantAccess?.access.allowed ?? false;
  const branchType = branch
    ? accessibleBranches.find((entry) => entry.id === branch.id)?.branchType ?? null
    : null;

  // Track whether we've identified at least once this session so we
  // know whether `reset()` on sign-out actually has anything to clear.
  // Without this, every page load with `isSignedIn = false` would
  // trigger a redundant reset() on the SDK.
  const hasIdentifiedRef = React.useRef(false);

  React.useEffect(() => {
    if (!authMeResolved || !tenantAccessResolved || !localUserId) return;

    posthog?.identify(localUserId);
    hasIdentifiedRef.current = true;

    // No tenant access (e.g. on /no-access) → do not group the user into a workspace/branch they cannot enter.
    if (!accessAllowed) return;

    if (workspace) {
      posthog?.group("workspace", workspace.id, {
        name: workspace.name,
        // The backend doesn't expose a billing `plan` yet (revisit when
        // billing lands); `structureType` is the closest organisational
        // descriptor we already have.
        structureType: workspace.structureType,
      });
    }

    if (branch) {
      posthog?.group("branch", branch.id, {
        name: branch.name,
        branchType: branchType ?? "UNKNOWN",
      });
    }
  }, [
    authMeResolved,
    tenantAccessResolved,
    localUserId,
    accessAllowed,
    workspace?.id,
    workspace?.name,
    workspace?.structureType,
    branch?.id,
    branch?.name,
    branchType,
  ]);

  React.useEffect(() => {
    if (isSignedIn === false && hasIdentifiedRef.current) {
      posthog?.reset();
      hasIdentifiedRef.current = false;
    }
  }, [isSignedIn]);

  return null;
}
